/**
 * Offline print notification
 *
 * Surfaces a single keyed antd notification when a print attempt is skipped
 * because QZ Tray is offline or no printer is selected. Offers "Connect
 * printer & retry" and "Open in new tab" actions
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback } from 'react';
import { Button, notification, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { downloadLabelUrl } from './useLabelPrinting';
import { usePrinterConnection } from './usePrinterConnection';
var NOTIFICATION_KEY = 'label-printer-offline';
export var useOfflinePrintNotification = function () {
    var t = useTranslation().t;
    var connectPrinter = usePrinterConnection().connectPrinter;
    var show = useCallback(function (params) {
        var reason = params.reason, labelUrls = params.labelUrls, context = params.context, onRetry = params.onRetry, onOpenSettings = params.onOpenSettings;
        var title = reason === 'qz-disconnected'
            ? t('Printer not connected')
            : t('No printer selected');
        var description = reason === 'qz-disconnected'
            ? context
                ? t('{{context}} was not printed because QZ Tray is not connected.', { context: context })
                : t('Labels were not printed because QZ Tray is not connected.')
            : context
                ? t('{{context}} was not printed because no printer is selected.', { context: context })
                : t('Labels were not printed because no printer is selected.');
        var handleConnectAndRetry = function () { return __awaiter(void 0, void 0, void 0, function () {
            var next;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, connectPrinter(false)];
                    case 1:
                        _a.sent();
                        if (!onRetry) return [3 /*break*/, 3];
                        return [4 /*yield*/, onRetry()
                            // If the retry came back skipped again, leave the user with the
                            // notification they're already looking at — don't stack a new one.
                        ];
                    case 2:
                        next = _a.sent();
                        // If the retry came back skipped again, leave the user with the
                        // notification they're already looking at — don't stack a new one.
                        if (next && next.status === 'printed') {
                            notification.close(NOTIFICATION_KEY);
                        }
                        return [2 /*return*/];
                    case 3:
                        notification.close(NOTIFICATION_KEY);
                        return [2 /*return*/];
                }
            });
        }); };
        var handleOpenInTab = function () {
            labelUrls.forEach(function (url) { return url && downloadLabelUrl(url); });
            notification.close(NOTIFICATION_KEY);
        };
        var buttons = [];
        if (reason === 'qz-disconnected') {
            buttons.push(React.createElement(Button, {
                key: 'retry',
                size: 'small',
                type: 'primary',
                onClick: handleConnectAndRetry
            }, t('Connect printer & retry')));
        }
        else if (onOpenSettings) {
            buttons.push(React.createElement(Button, { key: 'settings', size: 'small', type: 'primary', onClick: onOpenSettings }, t('Printer settings')));
        }
        if (labelUrls.some(Boolean)) {
            var openLabel = labelUrls.length > 1
                ? t('Open {{count}} in new tabs', { count: labelUrls.length })
                : t('Open in new tab');
            buttons.push(React.createElement(Button, { key: 'open', size: 'small', onClick: handleOpenInTab }, openLabel));
        }
        notification.warning({
            key: NOTIFICATION_KEY,
            message: title,
            description: description,
            duration: 0,
            btn: React.createElement.apply(React, __spreadArray([Space, null], buttons, false))
        });
    }, [connectPrinter, t]);
    var promptIfOffline = useCallback(function (outcome, opts) {
        var _a;
        if (opts === void 0) { opts = {}; }
        if (outcome.status !== 'skipped')
            return;
        show({
            reason: outcome.reason,
            labelUrls: (_a = opts.labelUrls) !== null && _a !== void 0 ? _a : [outcome.labelUrl],
            context: opts.context,
            onRetry: opts.onRetry,
            onOpenSettings: opts.onOpenSettings
        });
    }, [show]);
    var promptOfflineBulk = useCallback(function (_a) {
        var reason = _a.reason, labelUrls = _a.labelUrls, context = _a.context, onRetry = _a.onRetry, onOpenSettings = _a.onOpenSettings;
        show({
            reason: reason,
            labelUrls: labelUrls,
            context: context,
            onRetry: onRetry ? function () { return __awaiter(void 0, void 0, void 0, function () { return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, onRetry()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            }); }); } : undefined,
            onOpenSettings: onOpenSettings
        });
    }, [show]);
    return { promptIfOffline: promptIfOffline, promptOfflineBulk: promptOfflineBulk };
};
export default useOfflinePrintNotification;
